<template>
  <div class="cursor-pointer">
    <div class="shadow-md rounded-md bg-white flex flex-col">
      <div class="h-64 w-full relative bg-white rounded-t-md overflow-hidden">
        <img
          :key="`${detail?.id}-${safeThumbnail}`"
          :src="safeThumbnail"
          class="h-full w-full object-fill"
          alt=""
          @error="onImgError"
        />

        <template v-if="hasVideo">
          <div class="absolute inset-0 rounded-md bg-[rgba(0,0,0,0.45)]"></div>

          <IconPlay
            class="absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[1]"
          />
        </template>
      </div>
      <p class="mt-4 mx-3 text-black mb-1 truncate" :title="detail?.location">
        {{ detail?.location }}
      </p>
      <div class="mx-3 mb-3 min-w-0">
        <div class="flex items-center gap-2 min-w-0">
          <p
            class="flex-1 min-w-0 text-main-color font-bold text-sm truncate"
            :title="getTitleFull()"
          >
            {{ getTitleMain() }}
          </p>

          <span
            v-if="getTitleRight()"
            class="shrink-0 text-main-color font-bold text-sm"
            :title="getTitleRight()"
          >
            {{ getTitleRight() }}
          </span>
        </div>
      </div>

      <p v-if="shouldShowPrice" class="mx-3 mb-3 text-[#409DBA] font-bold text-lg">
        {{ formatNumber(detail?.price) }} TMT
      </p>
      <button
        v-if="detail?.subscription !== ''"
        class="w-full rounded-b-md font-bold"
        :class="getSubcriptionType?.color"
      >
        {{ getSubcriptionType?.name }}
      </button>
      <span v-else>&emsp;</span>
      <p class="font-bold m-3">
        {{ $t('page.status') }}:
        <span :class="statusView.class">
          {{ statusView.label }}
        </span>
      </p>
    </div>
  </div>
</template>

<script>
import { MYPUBLICATION_STATUSES } from '@/constants/index.js';
import IconPlay from '@icons/IconPlay.vue';
import { formatNumber, isVideo } from '@/helpers';

export default {
  name: 'MyPublicationCard',
  components: { IconPlay },
  props: {
    detail: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      MYPUBLICATION_STATUSES: MYPUBLICATION_STATUSES,
    };
  },
  methods: {
    formatNumber,

    onImgError(event) {
      if (!event?.target) return;

      const fallback = '/placeholder-publication.png';

      if (!event.target.src.includes(fallback)) {
        event.target.src = fallback;
      }
    },

    isYearLike(v) {
      const s = String(v ?? '').trim();
      return /^\d{4}$/.test(s);
    },

    getTitleParts() {
      const arr = Array.isArray(this.detail?.title_properties) ? this.detail.title_properties : [];
      const props = arr
        .map(p => p?.value)
        .filter(Boolean)
        .map(v => String(v).trim())
        .filter(Boolean);

      // ✅ если title_properties есть — используем их
      if (props.length) return props;

      // ✅ если нет title_properties — используем name
      const n = String(this.detail?.name ?? '').trim();
      return n ? [n] : [];
    },

    getTitleMain() {
      const parts = this.getTitleParts();
      if (parts.length >= 2 && this.isYearLike(parts[parts.length - 1])) {
        return parts.slice(0, -1).join(' ');
      }
      return parts.join(' ');
    },

    getTitleRight() {
      const parts = this.getTitleParts();
      if (parts.length >= 2 && this.isYearLike(parts[parts.length - 1])) {
        return parts[parts.length - 1];
      }
      return '';
    },

    getTitleFull() {
      return this.getTitleParts().join(' ');
    },
  },

  computed: {
    safeThumbnail() {
      const thumb = String(this.detail?.thumbnail ?? '').trim();

      if (!thumb || thumb === 'None' || thumb === 'null' || thumb === 'undefined') {
        return '/placeholder-publication.png';
      }

      return thumb;
    },

    shouldShowPrice() {
      if (
        this.detail?.category_has_price !== undefined &&
        this.detail?.category_has_price !== null
      ) {
        return !!this.detail.category_has_price;
      }

      if (
        this.detail?.category?.has_price !== undefined &&
        this.detail?.category?.has_price !== null
      ) {
        return !!this.detail.category.has_price;
      }

      const n = Number(this.detail?.price);
      return Number.isFinite(n) && n > 0;
    },

    hasVideo() {
      if (this.detail?.has_video !== undefined && this.detail?.has_video !== null) {
        return !!this.detail.has_video;
      }

      const medias = Array.isArray(this.detail?.images) ? this.detail.images : [];
      return medias.some(m => isVideo(m?.path));
    },

    getSubcriptionType() {
      switch (this.detail?.subscription) {
        case 'Gold':
          return { color: 'bg-[#FABB18] text-black', name: 'Gold' };
        case 'Silver':
          return { color: 'bg-[#C0C0C0] text-black', name: 'Silver' };
        case 'Bronze':
          return { color: 'bg-[#AE7145] text-white', name: 'Bronze' };
        default:
          return null;
      }
    },

    rejectionReasonText() {
      return this.detail?.standard_rejection_reason || this.detail?.rejection_reason || '';
    },

    statusView() {
      const locale = this.$i18n?.locale || 'ru';

      if (this.rejectionReasonText) {
        return {
          class: 'text-red-500',
          label: locale === 'tk' ? 'Ret edildi' : 'Отклонён',
        };
      }

      const fallback = this.MYPUBLICATION_STATUSES?.[this.detail?.status];
      return {
        class: fallback?.class || 'text-yellow-500',
        label: fallback?.label?.[locale] || (locale === 'tk' ? 'Garaşylýar' : 'Ожидается'),
      };
    },
  },
};
</script>

<style scoped></style>
